import _ from "lodash";
/** external */
import React, { PureComponent, Fragment } from "react";
import { compose, graphql } from "lib/apolloClient";
import {
  PencilLineOutlined,
  CornersOutOutlined,
  PageGridOutlined,
} from "@toddle-edu/ds-icons";
import { Button, IconButton } from "@toddle-edu/ds-web";
import PropTypes from "prop-types";
import classNames from "classnames";
/** internal */
import Workbook from "AppComponents/Workbook";
import UIModal from "UIComponents/UIModal";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import { WORKBOOK_MODE_TYPES } from "Utils";
import { isSafeExamBrowser } from "Utils/DeviceDetectHelpers";
import {
  getAttachment,
  deleteAttachment,
  createAttachment,
} from "modules/Services";
import { connect } from "react-redux";
import {
  updateQuestionSubmission,
  updateLoadingState,
} from "Worksheet/modules/WorksheetModules";
import {
  updateQuestionFieldsInCache,
  sendMessageToParentApp,
  isWorksheetWebView,
} from "Worksheet/modules/WorksheetUtils";
import {
  FIELD_UID_WORKBOOK_TEMPLATE,
  FIELD_UID_WORKBOOK_RESPONSE,
} from "Worksheet/modules/WorksheetConstants";
import { getAttachmentQuery } from "modules/CommonQuery";
import { getAttachmentFromCache } from "modules/CommonGraphqlHelpers";
import Loading from "UIComponents/Loading";
import ACLStore from "lib/aclStore";
/** relative */
import classes from "./DrawingField.scss";
import DrawingSideBar from "./DrawingSideBar";
import WorksheetCarousel from "./WorksheetCarousel";
import { getUserType } from "modules/CommonSelectors";
import { STUDENT, STAFF } from "Constants/stringConstants";

const styles = {
  containerStyle: {
    height: "100%",
  },
  loadingStyle: {
    width: "32",
    height: "32",
  },
};

class DrawingField extends PureComponent {
  constructor(props) {
    super(props);
    this.isWorksheetWebView = isWorksheetWebView();
    this.state = {
      showWorkbookModal: false,
      showQuestionDetailsSidebar: false,
      attachment: _.get(this.props, "value", {}),
      pageNumber: 1,
      isUrlLoading: false,
    };
  }

  componentDidMount() {
    if (this.isWorksheetWebView) {
      window.addEventListener("message", this.onMessageFromMobile);
    }
  }

  componentWillUnmount() {
    if (this.isWorksheetWebView) {
      window.removeEventListener("message", this.onMessageFromMobile);
    }
  }

  onMessageFromMobile = event => {
    const { data } = event;

    if (_.includes(data, "OPEN_LINK")) {
      const messageData = _.split(data, "OPEN_LINK:");
      const messagePayload = JSON.parse(_.get(messageData, "[1]", ""));
      sendMessageToParentApp("OPEN_LINK", messagePayload);
    } else if (_.includes(data, "UPDATE_SUCCESSFUL")) {
      this.onWorkbookUpdateSuccessfully();
    }
  };

  toggleWorkbookModal = value => {
    // if inside workbook, toggle should always work to get out of workbook.
    const { attachment, showWorkbookModal } = this.state;
    if (_.isEmpty(attachment)) return;

    const url = new URL(window.location.href);
    const isWorksheetWebView = url?.searchParams?.get("isWorksheetWebView");
    const canOpenWorkbookTool = ACLStore.can(
      "FeatureFlag:WorksheetOpenWorkbookToolInMobile"
    );
    const {
      isStudentWorkbookForceViewMode,
      mode,
      allowWorkbookInteraction,
    } = this.props;

    if (isWorksheetWebView && canOpenWorkbookTool) {
      const isEditMode =
        allowWorkbookInteraction &&
        !isStudentWorkbookForceViewMode &&
        _.isEqual(mode, "edit");
      sendMessageToParentApp("OPEN_WORKBOOK", { attachment, isEditMode });
      return;
    }
    if (!_.isNil(value)) {
      this.setState({ showWorkbookModal: value });
      return;
    }
    this.setState({ showWorkbookModal: !showWorkbookModal });
  };

  setViewQuestion = () => {
    this.setState({
      showQuestionDetailsSidebar: !this.state.showQuestionDetailsSidebar,
    });
  };

  updateAttachment = newAttachment =>
    this.setState({ attachment: newAttachment });

  updatePageNumber = pageNumber => {
    this.setState({ pageNumber });
  };

  updateIsUrlLoading = isUrlLoading => {
    this.setState({ isUrlLoading });
  };

  updateAttachmentData = async attachmentId => {
    const { fieldId, isQueriesV2Enabled } = this.props;

    if (!_.isEmpty(attachmentId)) {
      const fetchedAttachment = await getAttachment({ id: attachmentId });
      const updatedValue = [
        {
          id: fieldId,
          resolveFields: {
            id: fieldId,
            attachments: [{ ...fetchedAttachment }],
          },
        },
      ];

      this.updateAttachment(fetchedAttachment);
      updateQuestionFieldsInCache({ fields: updatedValue, isQueriesV2Enabled });
    }
  };

  componentDidUpdate(prevProps) {
    const { value, parentId } = this.props;
    if (!_.isEqual(_.get(prevProps, "parentId"), parentId)) {
      this.updateAttachment(value);
    }
  }

  onWorkbookDiscardDirectly = data => {
    this.setState({
      showQuestionDetailsSidebar: false,
    });
    // sending false to make sure that workbook is closed every time discard directly is called
    this.toggleWorkbookModal(false);
  };

  onWorkbookUpdateSuccessfully = async data => {
    const { updateLoadingState, updateInputField } = this.props;
    const { attachment } = this.state;

    const attachmentId = !_.isEmpty(data)
      ? _.get(data, "id", "")
      : _.get(attachment, "id", "");
    updateLoadingState(true);
    await this.updateAttachmentData(attachmentId);
    updateInputField({}); // To update attempted question logic in cache
    updateLoadingState(false);
  };

  updateQuestionSubmissionCall = async () => {
    const {
      updateQuestionSubmission,
      submissionId,
      fieldId,
      fieldKey,
      updateLoadingState,
      isQueriesV2Enabled,
    } = this.props;

    const fieldsToUpdate = [
      {
        id: fieldId,
        uid: fieldKey,
      },
    ];
    updateLoadingState(true);
    const submissionData = await updateQuestionSubmission({
      id: submissionId,
      fields: fieldsToUpdate,
      fetchFields: true,
    });
    const fields = _.get(
      submissionData,
      "data.content.updateQuestionSubmission.fields"
    );
    const filteredField = _.filter(fields, field => {
      return _.get(field, "resolveFields.__typename") === "QuestionAttachment";
    });
    const attachments = _.get(
      filteredField,
      "[0].resolveFields.attachments",
      ""
    );

    if (!_.isEmpty(attachments)) {
      const newAttachment = _.find(attachments, item =>
        _.isEqual("QUESTION_SUBMISSION_ATTACHMENT", item?.parentType)
      );
      this.updateAttachment(newAttachment);
      const updatedValue = [
        {
          id: fieldId,
          resolveFields: {
            id: fieldId,
            attachments,
          },
        },
      ];
      updateQuestionFieldsInCache({ fields: updatedValue, isQueriesV2Enabled });
    }
    updateLoadingState(false);
    this.toggleWorkbookModal();
  };

  handleButtonClick = async () => {
    const {
      questionFields,
      mode,
      submissionFields,
      createAttachment,
    } = this.props;
    const config = this.getDrawingQuestionConfig();
    const { workbookParentId, workbookParentType, isWorkbookEditable } = config;
    const { attachment } = this.state;

    const hasWorkbookAddedInitially = !_.isEmpty(
      _.get(
        _.find(questionFields, { uid: FIELD_UID_WORKBOOK_TEMPLATE }),
        "resolveFields.attachments[0]",
        ""
      )
    );
    const hasStudentAttemptedQuestion = !_.isEmpty(
      _.get(
        _.find(submissionFields, {
          uid: FIELD_UID_WORKBOOK_RESPONSE,
        }),
        "resolveFields.attachments[0]",
        ""
      )
    );
    if (
      _.includes(["edit", "evaluate"], mode) &&
      !hasStudentAttemptedQuestion &&
      hasWorkbookAddedInitially &&
      isWorkbookEditable
    ) {
      this.updateQuestionSubmissionCall();
    } else {
      if (_.isEmpty(attachment)) {
        const newAttachment = await createAttachment({
          attachment: {
            type: "WORKBOOK",
            name: "Workbook",
            metadata: {
              isWorkbookWithPageView: true,
              isNewBackgroundToolbarEnabled: true,
              isNewShapesToolbarEnabled: true,
              numOfPages: 1,
            },
          },
          nodeId: workbookParentId,
          parentType: workbookParentType,
          portalType: "PLANNER",
        });

        const workbookAttachment = _.find(newAttachment, obj =>
          _.isEqual(_.get(obj, "type"), "WORKBOOK")
        );

        if (!_.isEmpty(workbookAttachment)) {
          this.updateAttachment(workbookAttachment);
        }
      }
      this.toggleWorkbookModal();
    }
  };
  getWorkbookMode = () => {
    const {
      mode,
      allowWorkbookInteraction,
      userType,
      isStudentWorkbookForceViewMode,
    } = this.props;

    if (
      userType === STUDENT &&
      mode === "edit" &&
      allowWorkbookInteraction &&
      !isStudentWorkbookForceViewMode
    ) {
      if (isSafeExamBrowser()) {
        return WORKBOOK_MODE_TYPES["WORKSHEET_STUDENT_SEB_EDIT"];
      }
      return WORKBOOK_MODE_TYPES["WORKSHEET_STUDENT_EDIT"];
    }

    if (userType === STAFF) {
      switch (mode) {
        case "evaluate":
          return WORKBOOK_MODE_TYPES["WORKSHEET_TEACHER_EVALUATE"];
        case "create":
          return WORKBOOK_MODE_TYPES["WORKSHEET_TEACHER_CREATE"];
        case "preview":
          if (allowWorkbookInteraction) {
            return WORKBOOK_MODE_TYPES["WORKSHEET_STUDENT_EDIT"];
          }
          break;
      }
    }

    return WORKBOOK_MODE_TYPES["WORKBOOK_MULTIPAGE_VIEW"];
  };

  getDrawingQuestionConfig = numOfPages => {
    const {
      mode,
      allowWorkbookInteraction,
      parentId,
      isStudentWorkbookForceViewMode,
    } = this.props;
    let disableArr = ["EXIT_BTN"];
    let workbookParentId = null;
    const isWorkbookEditable =
      _.includes(["create", "evaluate"], mode) ||
      (_.includes(["edit", "preview"], mode) && allowWorkbookInteraction);
    const showQuestionTitle = !_.includes(["create"], mode);
    const hideSaveButton = _.includes(["preview"], mode);
    const workbookMode = this.getWorkbookMode();
    const workbookParentType =
      mode == "create"
        ? "QUESTION_ATTACHMENT"
        : "QUESTION_SUBMISSION_ATTACHMENT";
    if (mode != "preview") {
      workbookParentId = parentId;
    }
    if (hideSaveButton) {
      disableArr = _.concat(disableArr, "SAVE_BTN");
    }
    const workbookInteractionButtonLocale =
      isWorkbookEditable && !isStudentWorkbookForceViewMode
        ? _.isEqual(mode, "edit")
          ? "classRoom:add_response"
          : "common:edit"
        : "common:view";
    const showIconButton = _.isEqual(mode, "library_view");

    return {
      isWorkbookEditable,
      showQuestionTitle,
      workbookMode,
      workbookParentId,
      disableArr,
      workbookParentType,
      workbookInteractionButtonLocale,
      showIconButton,
    };
  };

  renderWorkbook = params => {
    const {
      portalType,
      questionDetails,
      mode,
      canEditAddedObjects,
      value,
    } = this.props;
    const {
      showQuestionTitle,
      workbookMode,
      disableArr,
      workbookParentType,
      workbookParentId,
    } = params;

    const updatedQuestionDetails = {
      questionFieldMode: mode,
      canEditAddedObjects,
      parentId: workbookParentId,
      parentType: workbookParentType,
    };
    const { attachment } = this.state;

    if (showQuestionTitle) {
      updatedQuestionDetails["questionData"] = questionDetails;
    }

    const url = new URL(window.location.href);
    const isWorksheetWebView = url?.searchParams?.get("isWorksheetWebView");

    return (
      <UIModal isFullScreen={true} isOpen={true}>
        <Workbook
          workbookMode={workbookMode}
          attachment={attachment}
          onUpdateSuccessfully={this.onWorkbookUpdateSuccessfully}
          onDiscardDirectly={this.onWorkbookDiscardDirectly}
          portalType={portalType}
          enableArr={["QUESTION_INFO"]}
          disableArr={disableArr}
          isDrawingQuestion={true}
          isWorksheetWebView={isWorksheetWebView}
          questionDetails={updatedQuestionDetails}
          setViewQuestion={this.setViewQuestion}
        />
      </UIModal>
    );
  };

  render() {
    const {
      t,
      value,
      labelComponent,
      questionId,
      isSubQuestion,
      subQuestionParentId,
      resourceId,
      questionIndex,
      updatedAttachmentData,
      isDrawingPreviewEnabled,
      isStudentWorkbookForceViewMode,
      worksheetMode,
      isFromInternalTool,
      isQueriesV2Enabled,
    } = this.props;
    const {
      showWorkbookModal,
      showQuestionDetailsSidebar,
      pageNumber,
      isUrlLoading,
      attachment,
    } = this.state;

    const thumbUrl = _.get(attachment, "thumbUrl", "");
    const numOfPages = _.get(attachment, "metadata.numOfPages", 0);

    const pageThumbUrls = _.get(
      _.get(updatedAttachmentData, `metadata.pageThumbUrls`),
      _.get(updatedAttachmentData, "metadata.workbookUrl"),
      []
    );

    const showLoader =
      isUrlLoading && numOfPages > 1 && _.isEmpty(pageThumbUrls);
    const showDrawingPreview = isDrawingPreviewEnabled && numOfPages > 1;

    const config = this.getDrawingQuestionConfig(numOfPages);
    const {
      isWorkbookEditable,
      workbookInteractionButtonLocale,
      showIconButton,
    } = config;

    const topContainerClasses = classNames(classes.topContainer, {
      [classes.hasPage]: showDrawingPreview,
    });
    return (
      <Fragment>
        <div className={classes.container}>
          {React.isValidElement(labelComponent) ? labelComponent : null}
          <div className={classes.drawingContainer}>
            {showLoader ? (
              <Loading
                loadingStyle={styles.loadingStyle}
                containerStyle={styles.containerStyle}
              />
            ) : (
              <>
                {!_.isEmpty(thumbUrl) ? (
                  showDrawingPreview ? (
                    <WorksheetCarousel
                      updatePageNumber={this.updatePageNumber}
                      attachmentData={updatedAttachmentData}
                      updateIsUrlLoading={this.updateIsUrlLoading}
                    />
                  ) : (
                    <img src={thumbUrl} alt="" className={classes.img} />
                  )
                ) : null}
                <div className={topContainerClasses}>
                  {showDrawingPreview ? (
                    <div className={classes.noOfPagesTag}>
                      <PageGridOutlined size={"xxxx-small"} />
                      <div>{`${pageNumber}/${numOfPages}`}</div>
                    </div>
                  ) : null}
                  {showIconButton ? (
                    <IconButton
                      icon={<CornersOutOutlined />}
                      _legacy_size="small"
                      _legacy_variant="outlined-subtle"
                      size="small"
                      dsVersion="2.0"
                      variant="neutral"
                      type="outlined"
                      onClick={this.handleButtonClick}
                      testId={`worksheet-drawingQuestion-onExpandClick`}
                    />
                  ) : (
                    <Button
                      icon={
                        isWorkbookEditable &&
                        !isStudentWorkbookForceViewMode ? (
                          <PencilLineOutlined />
                        ) : (
                          <CornersOutOutlined />
                        )
                      }
                      dsVersion="2.0"
                      variant="progressive"
                      size="medium"
                      _legacy_variant={"progressive"}
                      _legacy_size={"small"}
                      onClick={this.handleButtonClick}
                      testId={`worksheet-drawingQuestion-onEditClick`}
                    >
                      {t(workbookInteractionButtonLocale)}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {showWorkbookModal ? this.renderWorkbook(config) : null}
        <DrawingSideBar
          showQuestionDetailsSidebar={showQuestionDetailsSidebar}
          questionId={questionId}
          isSubQuestion={isSubQuestion}
          subQuestionParentId={subQuestionParentId}
          resourceId={resourceId}
          questionIndex={questionIndex}
          setViewQuestion={this.setViewQuestion}
          t={t}
          worksheetMode={worksheetMode}
          isFromInternalTool={isFromInternalTool}
          isQueriesV2Enabled={isQueriesV2Enabled}
        />
      </Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { value } = ownProps;

  const isDrawingPreviewEnabled = ACLStore.can(
    "FeatureFlag:EnableDrawingQuestionPreview"
  );
  const attachmentId = _.get(value, "id");
  const userType = getUserType(state);
  return {
    attachmentId,
    isDrawingPreviewEnabled,
    userType,
  };
};
const mapActionCreators = {
  deleteAttachment,
  updateQuestionSubmission,
  updateLoadingState,
  createAttachment,
};

DrawingField.propTypes = {
  value: PropTypes.object,
  updateInputField: PropTypes.func,
  fieldKey: PropTypes.string,
  portalType: PropTypes.string,
  questionFields: PropTypes.array,
  mode: PropTypes.string,
  labelComponent: PropTypes.element,
  canEditAddedObjects: PropTypes.bool,
  questionIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  questionId: PropTypes.string,
  isSubQuestion: PropTypes.bool,
  subQuestionParentId: PropTypes.string,
  resourceId: PropTypes.string,
  submissionFields: PropTypes.array,
  allowWorkbookInteraction: PropTypes.bool,
  updatedAttachmentData: PropTypes.object,
  isStudentWorkbookForceViewMode: PropTypes.bool,
  isQueriesV2Enabled: PropTypes.bool,
};

DrawingField.defaultProps = {
  canEditAddedObjects: false,
  isSubQuestion: false,
  allowWorkbookInteraction: true,
  isQueriesV2Enabled: false,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAttachmentQuery, {
    name: "getAttachment",
    skip: ({ attachmentId, isDrawingPreviewEnabled }) =>
      _.isEmpty(attachmentId) || !isDrawingPreviewEnabled,
    options: ({ attachmentId }) => ({
      fetchPolicy: "cache-only",
      variables: {
        id: attachmentId,
      },
    }),
    props: ({ getAttachment, ownProps: { value } }) => {
      const { variables } = getAttachment;
      const cacheData = getAttachmentFromCache(variables);
      const updatedAttachmentData = !_.isEmpty(cacheData) ? cacheData : value;

      return {
        updatedAttachmentData,
        onPusherEventTriggered: () => {
          getAttachment.refetch();
        },
      };
    },
  }),
  withPusherBind(["pagesThumburlUpdated"])
)(DrawingField);
